import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Mail, Trash2, Globe, Heart, Server } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#030712]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to App</span>
        </Link>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="w-10 h-10 text-indigo-400" />
            <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          </div>
          
          <p className="text-muted-foreground mb-8">
            Last updated: February 2025
          </p>

          {/* Our Commitment */}
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-2 mb-3">
              <Heart className="w-6 h-6 text-indigo-400" />
              <h2 className="text-xl font-semibold text-white">Our Commitment to You</h2>
            </div>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center space-x-2">
                <span className="text-green-400">✓</span>
                <span>Your financial data belongs to you — we never sell it</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-400">✓</span>
                <span>Receipt photos are used only for text extraction</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-400">✓</span>
                <span>Bank-level encryption protects all your information</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-400">✓</span>
                <span>Delete your data anytime — no questions asked</span>
              </li>
            </ul>
          </div>

          <div className="space-y-8 text-gray-300">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Eye className="w-5 h-5 text-indigo-400" />
                <span>Introduction</span>
              </h2>
              <p>
                FinScan USA ("we", "our", or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you use our mobile application and web service for receipt 
                scanning and expense tracking.
              </p>
            </section>

            {/* Information We Collect - Apple Format */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Database className="w-5 h-5 text-indigo-400" />
                <span>Information We Collect</span>
              </h2>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Contact Info</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Name (for personalization)</li>
                    <li>Email address (for account access and communications)</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">User ID</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Account password (stored in encrypted form)</li>
                    <li>Internal account identifier</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Financial Info</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Payment information (processed securely via Stripe, not stored by us)</li>
                    <li>Subscription status</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Photos & Videos</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Receipt images you upload for scanning</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Purchases</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Transaction history from scanned receipts</li>
                    <li>Merchant names, dates, and amounts</li>
                    <li>Categories and tags you assign</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Usage Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>App interactions and feature usage</li>
                    <li>Device information and IP address</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Diagnostics</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Crash logs for troubleshooting</li>
                    <li>Performance data to improve the app</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Lock className="w-5 h-5 text-indigo-400" />
                <span>How We Use Your Information</span>
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong className="text-white">Receipt Processing:</strong> We use OCR technology to extract text from your receipt images</li>
                <li><strong className="text-white">Expense Tracking:</strong> To categorize and organize your financial data</li>
                <li><strong className="text-white">Account Management:</strong> To maintain your account and provide customer support</li>
                <li><strong className="text-white">Service Improvement:</strong> To analyze usage patterns and improve our app</li>
                <li><strong className="text-white">Communications:</strong> To send important updates about your account or subscription</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Globe className="w-5 h-5 text-indigo-400" />
                <span>Third-Party Services</span>
              </h2>
              <p className="mb-4">
                <strong className="text-green-400">We do NOT sell your personal data to third parties.</strong>
              </p>
              <p className="mb-4">We share data with trusted service providers who assist us in operating our app:</p>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">OpenAI (OCR Provider)</h3>
                  <p className="text-gray-400">
                    Receives receipt images <strong className="text-white">only for the purpose of extracting text</strong> to 
                    digitize your expenses. They are contractually prohibited from using your data for any other purpose, 
                    including AI model training.
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-2">Stripe (Payment Processor)</h3>
                  <p className="text-gray-400">
                    Handles all payment transactions securely. <strong className="text-white">We do not store your full 
                    credit card details</strong> on our servers. Stripe is PCI-DSS Level 1 certified.
                  </p>
                </div>
              </div>
              <p className="mt-4 text-gray-400">We may also share data when:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mt-2">
                <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong className="text-white">Business Transfers:</strong> In case of merger or acquisition (with prior notice to you)</li>
              </ul>
            </section>

            {/* Data Storage */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Server className="w-5 h-5 text-indigo-400" />
                <span>Data Storage & Security</span>
              </h2>
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-white mb-2">Storage Location</h3>
                <p className="text-gray-400">
                  Your information, including receipt images, is stored on secure servers located in the 
                  <strong className="text-white"> United States</strong>.
                </p>
              </div>
              <p className="mb-4">We implement industry-standard security measures:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>All data transmission is encrypted using SSL/TLS (256-bit)</li>
                <li>Passwords are hashed using bcrypt algorithm</li>
                <li>Receipt images are stored with strict access controls</li>
                <li>Payment processing via PCI-compliant Stripe</li>
                <li>Regular security audits and vulnerability assessments</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Trash2 className="w-5 h-5 text-indigo-400" />
                <span>Your Rights</span>
              </h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your account and all associated data</li>
                <li><strong className="text-white">Export:</strong> Download your data in a portable format</li>
                <li><strong className="text-white">Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, contact us at the address below.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Data Retention</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Account data: Retained while your account is active</li>
                <li>Receipt images: Stored until you delete them or close your account</li>
                <li>Transaction history: Kept for 7 years for tax compliance purposes</li>
                <li>After account deletion: Data is permanently removed within 30 days</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Children's Privacy</h2>
              <p>
                FinScan USA is not intended for children under 13 years of age. We do not 
                knowingly collect personal information from children. If you believe we have 
                collected data from a child, please contact us immediately.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center space-x-2">
                <Mail className="w-5 h-5 text-indigo-400" />
                <span>Contact Us</span>
              </h2>
              <p className="mb-4">
                If you have questions about this Privacy Policy or wish to exercise your data rights:
              </p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <p className="text-xl font-semibold text-white mb-3">FinScan USA</p>
                <p className="text-gray-400 mb-1">Nexus SS LLC</p>
                <p className="text-gray-400 mb-1">9701 Dee Rd apt 5K</p>
                <p className="text-gray-400 mb-3">Niles, IL 60714, USA</p>
                <p className="text-gray-400">
                  <strong className="text-white">Email:</strong>{' '}
                  <a href="mailto:Nexus.ss.llc@gmail.com" className="text-indigo-400 hover:underline">
                    Nexus.ss.llc@gmail.com
                  </a>
                </p>
              </div>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of 
                significant changes by email or through the app. Your continued use of FinScan USA 
                after changes constitutes acceptance of the updated policy.
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-muted-foreground text-sm">
              By using FinScan USA, you agree to this Privacy Policy.
            </p>
            <Link 
              to="/auth" 
              className="inline-block mt-4 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-full font-medium transition-colors"
            >
              Create Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
